import {
    Box,
    Button,
    Table,
    TableBody,
    TableHeader,
    TableRow,
    TableCell,
    TableColumn,
    HStack,
    Input,
    Text,
  } from "@chakra-ui/react";
  import { useState, useMemo } from "react";
  import { FiArrowDown, FiArrowUp } from "react-icons/fi";
  import PageHeader from "../components/PageHeader";
  import UserModal from "../components/UserModal";
  import { User } from "../types/User";
  
  const Users = () => {
    const [search, setSearch] = useState<string>("");
    const [sortKey, setSortKey] = useState<"name" | "email" | "role">("name"); // Explicit union type
    const [sortOrder, setSortOrder] = useState<"asc" | "desc">("asc");
    const [page, setPage] = useState(1);
    const usersPerPage = 5;
    const [modalOpen, setModalOpen] = useState(false);
    const [selectedUser, setSelectedUser] = useState<User | null>(null);
    const [users, setUsers] = useState<User[]>([
      { id: 1, name: "Alice Johnson", email: "alice@example.com", role: "Admin" },
      { id: 2, name: "Bob Smith", email: "bob@example.com", role: "Editor" },
      { id: 3, name: "Carol Davis", email: "carol@example.com", role: "Viewer" },
      { id: 4, name: "David Lee", email: "david@example.com", role: "Admin" },
      { id: 5, name: "Emma Watson", email: "emma@example.com", role: "Editor" },
      { id: 6, name: "Frank Green", email: "frank@example.com", role: "Viewer" },
    ]);
  
    const filteredUsers = useMemo(() => {
      return users.filter(
        (user) =>
          user.name.toLowerCase().includes(search.toLowerCase()) ||
          user.email.toLowerCase().includes(search.toLowerCase()) ||
          user.role.toLowerCase().includes(search.toLowerCase())
      );
    }, [search, users]);
  
    const sortedUsers = useMemo(() => {
      return [...filteredUsers].sort((a, b) => {
        const aValue = a[sortKey].toLowerCase(); // Simplified: No need for toString() or fallback
        const bValue = b[sortKey].toLowerCase();
        if (sortOrder === "asc") return aValue.localeCompare(bValue);
        return bValue.localeCompare(aValue);
      });
    }, [filteredUsers, sortKey, sortOrder]);
  
    const paginatedUsers = useMemo(() => {
      const start = (page - 1) * usersPerPage;
      return sortedUsers.slice(start, start + usersPerPage);
    }, [sortedUsers, page]);
  
    const toggleSort = (key: "name" | "email" | "role") => {
      if (sortKey === key) {
        setSortOrder((prev) => (prev === "asc" ? "desc" : "asc"));
      } else {
        setSortKey(key);
        setSortOrder("asc");
      }
    };
  
    const handleUserSubmit = (formData: User) => {
      if (formData.id) {
        setUsers((prev) =>
          prev.map((user) =>
            user.id === formData.id ? { ...user, ...formData } : user
          )
        );
      } else {
        setUsers((prev) => [
          ...prev,
          { ...formData, id: prev.length + 1 },
        ]);
      }
      setModalOpen(false);
      setSelectedUser(null);
    };
  
    return (
      <Box>
        <PageHeader
          title="Users"
          subtitle="Manage users who can access the application."
          rightElement={
            <UserModal
              initialData={null}
              onSubmit={handleUserSubmit}
              triggerElement={
                <Button
                  size="sm"
                  colorScheme="blue"
                  onClick={() => {
                    setSelectedUser(null);
                    setModalOpen(true);
                  }}
                >
                  Add User
                </Button>
              }
              open={modalOpen && !selectedUser}
              onOpenChange={({ open }) => setModalOpen(open)}
            />
          }
        />
  
        <Box mt={4} mb={4}>
          <Input
            placeholder="Search users..."
            value={search}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => setSearch(e.target.value)}
            maxW="300px"
          />
        </Box>
  
        <Box bg="white" borderRadius="md" boxShadow="sm" overflowX="auto">
          <Table.Root>
            <TableHeader>
              <TableRow>
                {["name", "email", "role"].map((key) => (
                  <TableColumn
                    as="th"
                    key={key}
                    cursor="pointer"
                    onClick={() => toggleSort(key as "name" | "email" | "role")}
                  >
                    <HStack gap="1">
                      <Box textTransform="capitalize">{key}</Box>
                      {sortKey === key && (
                        <Box>
                          {sortOrder === "asc" ? <FiArrowUp /> : <FiArrowDown />}
                        </Box>
                      )}
                    </HStack>
                  </TableColumn>
                ))}
                <TableColumn as="th" textAlign="right">
                  Actions
                </TableColumn>
              </TableRow>
            </TableHeader>
  
            <TableBody>
              {paginatedUsers.map((user) => (
                <TableRow key={user.id}>
                  <TableCell>{user.name}</TableCell>
                  <TableCell>{user.email}</TableCell>
                  <TableCell>{user.role}</TableCell>
                  <TableCell>
                    <HStack justify="flex-end" gap="2">
                      <UserModal
                        initialData={user}
                        onSubmit={handleUserSubmit}
                        triggerElement={
                          <Button
                            size="xs"
                            variant="outline"
                            colorScheme="blue"
                            onClick={() => {
                              setSelectedUser(user);
                              setModalOpen(true);
                            }}
                          >
                            Edit
                          </Button>
                        }
                        open={modalOpen && selectedUser?.id === user.id}
                        onOpenChange={({ open }) => {
                          setModalOpen(open);
                          if (!open) setSelectedUser(null);
                        }}
                      />
                      <Button size="xs" variant="ghost" colorScheme="red">
                        Delete
                      </Button>
                    </HStack>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table.Root>
        </Box>
  
        <HStack mt={4} justify="flex-end" gap="3">
          <Button
            size="sm"
            onClick={() => setPage((p) => Math.max(p - 1, 1))}
            disabled={page === 1}
          >
            Previous
          </Button>
          <Text>Page {page}</Text>
          <Button
            size="sm"
            onClick={() =>
              setPage((p) =>
                p < Math.ceil(sortedUsers.length / usersPerPage) ? p + 1 : p
              )
            }
            disabled={page === Math.ceil(sortedUsers.length / usersPerPage)}
          >
            Next
          </Button>
        </HStack>
      </Box>
    );
  };
  
  export default Users;